/**
 * ClassesPage Component
 * Displays and manages classes
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeachStore } from '../../stores/teachStore';
import { useStore } from '../../stores';
import { Plus, Loader2, UserPlus } from 'lucide-react';
import { ClassCard } from '../../components/teach/ClassCard';
import { ClassModal } from '../../components/teach/ClassModal';
import { MemberManager } from '../../components/teach/MemberManager';
import { MessageDialog } from '../../components/common/MessageDialog';
import type { Class } from '@shared/types/teach';

export const ClassesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useStore();
  const isParent = user?.role === 'parent';
  const {
    classes,
    classesLoading,
    fetchClasses,
    createClass,
    updateClass,
    deleteClass,
    setSelectedClass,
    selectedClass,
  } = useTeachStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Message dialog state
  const [messageDialog, setMessageDialog] = useState<{
    isOpen: boolean;
    title?: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showMessage = (message: string, type: 'success' | 'error' | 'info' = 'info', title?: string) => {
    setMessageDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeMessageDialog = () => {
    setMessageDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Load classes on mount
  useEffect(() => {
    fetchClasses().catch(err => {
      showMessage('加载班级列表失败', 'error');
    });
  }, []);

  const handleCreate = async (data: { name: string; description: string }) => {
    try {
      await createClass(data);
      setShowCreateModal(false);
      showMessage('班级创建成功', 'success');
    } catch (error: any) {
      showMessage(error.message || '创建班级失败', 'error');
    }
  };

  const handleEdit = (cls: Class) => {
    setSelectedClass(cls);
    setShowEditModal(true);
  };

  const handleUpdate = async (data: { name: string; description: string }) => {
    if (!selectedClass) return;

    try {
      await updateClass(selectedClass.id, data);
      setShowEditModal(false);
      setSelectedClass(null);
      showMessage('班级更新成功', 'success');
    } catch (error: any) {
      showMessage(error.message || '更新班级失败', 'error');
    }
  };

  const handleManageMembers = (cls: Class) => {
    setSelectedClass(cls);
    setShowMemberModal(true);
  };

  const handleDeleteConfirm = (classId: string) => {
    setShowDeleteConfirm(classId);
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      await deleteClass(showDeleteConfirm);
      setShowDeleteConfirm(null);
      showMessage('班级删除成功', 'success');
    } catch (error: any) {
      showMessage(error.message || '删除班级失败', 'error');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleCloseMemberModal = () => {
    setShowMemberModal(false);
    setSelectedClass(null);
    // Refresh classes to update member counts
    fetchClasses();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isParent ? '家庭成员' : '班级管理'}
        </h1>
        {isParent ? (
          <button
            onClick={() => navigate('/family/add-child')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            添加家庭成员
          </button>
        ) : (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            创建班级
          </button>
        )}
      </div>

      {/* Classes grid */}
      {classesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : classes.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            还没有创建任何班级
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            创建第一个班级
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((cls) => (
            <ClassCard
              key={cls.id}
              class={cls}
              onEdit={() => handleEdit(cls)}
              onManageMembers={() => handleManageMembers(cls)}
              onDelete={() => handleDeleteConfirm(cls.id)}
            />
          ))}
        </div>
      )}

      {/* Create class modal */}
      {showCreateModal && (
        <ClassModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
        />
      )}

      {/* Edit class modal */}
      {showEditModal && selectedClass && (
        <ClassModal
          class={selectedClass}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClass(null);
          }}
          onSubmit={handleUpdate}
        />
      )}

      {/* Member manager modal */}
      {showMemberModal && selectedClass && (
        <MemberManager
          class={selectedClass}
          onClose={handleCloseMemberModal}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              确认删除
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              确定要删除这个班级吗？删除后班级记录和成员关系将被移除，但不会影响学生账户。
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message dialog */}
      <MessageDialog
        isOpen={messageDialog.isOpen}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
        onClose={closeMessageDialog}
      />
    </div>
  );
};
